import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Heart,
  Instagram,
  MessageCircle
} from 'lucide-react'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'

const Footer: React.FC = () => {
  const quickLinks = [
    { name: 'Ana Sayfa', href: '/' },
    { name: 'Bizi Tanıyın', href: '/about' },
    { name: 'Acil Tedavi', href: '/hizmetler/acil-tedavi' },
    { name: 'Check-Up', href: '/hizmetler/check-up' },
    { name: 'İletişim', href: '/contact' }
  ]

  const services = [
    { name: 'Cerrahi', href: '/hizmetler/cerrahi' },
    { name: 'Kardiyoloji', href: '/hizmetler/kardiyoloji' },
    { name: 'Dermatoloji', href: '/hizmetler/dermatoloji' },
    { name: 'Pet Oteli', href: '/klinik/pet-oteli' },
    { name: 'Pet Kuaför', href: '/klinik/pet-kuafor' }
  ]

  const diseases = [
    { name: 'Köpek Hastalıkları', href: '/hastaliklari/kopek' },
    { name: 'Kedi Hastalıkları', href: '/hastaliklari/kedi' },
    { name: 'Köpeklerde İshal', href: '/hastaliklari/kopek/kopeklerde-ishal' },
    { name: 'Kedilerde Astım', href: '/hastaliklari/kedi/kedilerde-astim' }
  ]

  return (
    <footer className="bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 text-white">
      <div className="container-custom">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="/logo.webp"
                  alt="Ardıç Veteriner Kliniği"
                  className="h-12 w-auto filter brightness-0 invert"
                />
                <div>
                  <h3 className="text-xl font-bold">Ardıç Veteriner</h3>
                  <p className="text-primary-200">Kliniği</p>
                </div>
              </div>
              <p className="text-primary-100 mb-6 leading-relaxed">
                Şanlıurfa'nın en modern veteriner kliniği olarak, 
                can dostlarınızın sağlığı için 7/24 hizmet veriyoruz.
              </p>
              
              {/* Social Media */}
              <div className="flex space-x-4">
                {contactInfo.socialMedia.instagram && (
                  <motion.a
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    href={contactInfo.socialMedia.instagram}
                    className="p-2 bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors duration-200"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram className="w-5 h-5" />
                  </motion.a>
                )}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h4 className="text-lg font-semibold mb-6">Hızlı Linkler</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-primary-100 hover:text-white transition-colors duration-200 flex items-center space-x-2 group"
                    >
                      <Heart className="w-3 h-3 group-hover:text-accent-400 transition-colors duration-200" />
                      <span>{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="text-lg font-semibold mb-6">Hizmetlerimiz</h4>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.href}
                      className="text-primary-100 hover:text-white transition-colors duration-200 flex items-center space-x-2 group"
                    >
                      <Heart className="w-3 h-3 group-hover:text-accent-400 transition-colors duration-200" />
                      <span>{service.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="text-lg font-semibold mb-6">İletişim</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-accent-400 mt-0.5 flex-shrink-0" />
                  <p className="text-primary-100 text-sm leading-relaxed">
                    {contactInfo.address}
                  </p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <a
                    href={toTelHref(contactInfo.phone)}
                    className="text-primary-100 hover:text-white transition-colors duration-200"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-primary-100 hover:text-white transition-colors duration-200"
                  >
                    {contactInfo.email}
                  </a>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-accent-400 mt-0.5 flex-shrink-0" />
                  <div className="text-primary-100 text-sm">
                    <p>Hafta içi: {contactInfo.workingHours.weekdays}</p>
                    <p>Hafta sonu: {contactInfo.workingHours.weekend}</p>
                    <p className="text-accent-300 font-semibold">
                      {contactInfo.workingHours.emergency}
                    </p>
                  </div>
                </div>
              </div>

              {/* WhatsApp Button */}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={toWhatsAppHref(contactInfo.whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 mt-4 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp</span>
              </motion.a>
            </motion.div>
          </div>
        </div>

        {/* Disease Guide Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-t border-primary-600 py-8"
        >
          <h4 className="text-lg font-semibold mb-6">Hastalık Rehberi</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {diseases.map((disease) => (
              <Link
                key={disease.name}
                to={disease.href}
                className="text-primary-100 hover:text-white transition-colors duration-200 text-sm"
              >
                {disease.name}
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-600 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-primary-200 text-sm">
              © 2024 Ardıç Veteriner Kliniği. Tüm hakları saklıdır.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link
                to="/privacy"
                className="text-primary-200 hover:text-white transition-colors duration-200"
              >
                Gizlilik Politikası
              </Link>
              <Link
                to="/terms"
                className="text-primary-200 hover:text-white transition-colors duration-200"
              >
                Kullanım Şartları
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
